import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Order } from "../types/order";
import { ConferenceGroup } from "../types/conferenceGroup";
import ContactInformationForm from "./registration/ContactInformation";
import BillingInformationForm from "./registration/BillingInformation";
import RoomRequirementsForm from "./registration/RoomRequirements";
import HotelSelector from "./registration/HotelSelector";
import { roomSelectorErrors } from "../lib/validate";
import LiveOrder from "../components/LiveOrder";
import RoomSelector from "./registration/RoomSelector";
import RoomOccupants from "./registration/RoomOccupants";
import HotelPaperwork from "./registration/HotelPaperwork";
import OrderSummary from "./registration/OrderSummary";
import Confirmation from "./registration/Confirmation";

export default function RegistrationPage() {
  const [activeStep, setActiveStep] = useState(0);
  const [order, setOrder] = useState<Order>({contactFirstName: '', contactLastName: '', contactEmail: '', contactCell: '', billingCountry: 'US', termsAccepted: false} as Order);
  const [conferenceGroup, setConferenceGroup] = useState<ConferenceGroup | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderUpdateCounter, setOrderUpdateCounter] = useState(0);

  
  const [searchParams] = useSearchParams();
  const orderIdFromUrl = searchParams.get('orderId') || "";

  const handleSaveOrder = async () => {
    setOrderUpdateCounter(prev => prev + 1); // Trigger refresh of live order status on next step
  }

  const handleNextStep = async () => {
    if (activeStep === steps.length - 1) {
      //there is no next step
      return;
    }

    setOrderUpdateCounter(prev => prev + 1); // Trigger refresh of live order status on next step
    // Move to confirmation step
    setActiveStep(activeStep + 1);
  };

   const handlePreviousStep = () => {
    // Don't allow going back from confirmation step
    if (activeStep > 0) {
      setOrderUpdateCounter(prev => prev + 1); // Trigger refresh of live order status on previous step
      setActiveStep(activeStep - 1);
    }
  };

const steps = [

    {
        label: 'Room Requirements',
        content: <RoomRequirementsForm orderId={orderIdFromUrl} type="step" back={handlePreviousStep} onSave={handleNextStep} /> 
    },
    {
        label: 'Hotel Selection',
        content: <HotelSelector orderId={orderIdFromUrl} type="step" back={handlePreviousStep} onSave={handleNextStep} />
    },
    {
        label: 'Rooms',
        content: <RoomSelector  orderId={orderIdFromUrl} type="step" back={handlePreviousStep} onSave={handleSaveOrder} onStepComplete={handleNextStep} />
    },
    {
        label: 'Occupants',
        content: <RoomOccupants orderId={orderIdFromUrl} type="step" back={handlePreviousStep} onSave={handleSaveOrder} onStepComplete={handleNextStep} />
    },
     {
        label: 'Contact',
        content: <ContactInformationForm orderId={orderIdFromUrl} type="step" back={handlePreviousStep} onSave={handleNextStep} />
    },
    {
        label: 'Billing', 
        content: <BillingInformationForm orderId={orderIdFromUrl} type="step" back={handlePreviousStep} onSave={handleNextStep} />
    },
    {
        label: 'Hotel Paperwork',
        content: <HotelPaperwork orderId={orderIdFromUrl} type="step" back={handlePreviousStep} onSave={handleNextStep} />
    },
    {
        label: 'Review & Confirm',
        content: <OrderSummary orderId={orderIdFromUrl} type="step" back={handlePreviousStep} onSave={handleNextStep} />
    },
    {
        label: 'Confirmation',
        content: <Confirmation orderId={orderIdFromUrl} />
    }
  ];
  
  const isConfirmationStep = activeStep === steps.length - 1;

  return (
    <Box sx={{ minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ my: 4, position: 'relative' }}>
          <Link to="/">
            <IconButton sx={{ mb: 3, color: 'primary.main' }}>
              <ArrowBackIcon />
              <Typography variant="body2" sx={{ ml: 1 }}>
                Back to Home
              </Typography>
            </IconButton>
          </Link>
          {activeStep < steps.length - 1 && (
            <>
              <Typography variant="h3" component="h1" fontWeight="bold" sx={{ mb: 2 }}>
                Hotel Registration
              </Typography>
              {conferenceGroup && (
                <Typography variant="h6" color="text.secondary">
                  {conferenceGroup.conference.longName}
                </Typography>
              )}
            </>
          )}
          {loading && (
            <Typography variant="body2" color="primary">
              Loading conference data...
            </Typography>
          )}
          {error && (
            <Typography variant="body2" color="error">
              Error: {error}
            </Typography>
          )}
        </Box>

        
            {/* Show live order status when order exists and not on confirmation step */}
            {!isConfirmationStep && (
              <LiveOrder orderId={orderIdFromUrl} refreshTrigger={orderUpdateCounter} />
            )}
            
             {!isConfirmationStep && <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
                <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 2 }}>
                  {steps.map((step, index) => (
                    <Step key={step.label}>
                      <StepLabel>
                        <Typography variant="body2" sx={{ fontWeight: activeStep === index ? 600 : 400 }}>
                          {step.label}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        {step.content}
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </Paper>}
                  
                  {isConfirmationStep && (
                    <Box>
                      {steps[activeStep].content}
                    </Box>
                  )}
         
        
      </Container>
    </Box>
  );
}